import type { ReactElement, ReactNode, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { debounce } from 'throttle-debounce';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import { useGetChildrenProp } from '@alfa-bank/site-helpers-common/react/get-children-prop';
import type { TEvent } from '@alfa-bank/site-helpers-common/src/browser/send-event/models';

const getCards = (elem: HTMLDivElement) => {
    return Array.from(elem.firstChild?.childNodes ?? []) as HTMLDivElement[]; // gridContainer.grid.cols, способ достать все чилды не используя классы
};

const VISIBILITY_PERSENTAGE = 50;

export const isElementInViewport = (container: HTMLDivElement, node: HTMLDivElement): boolean => {
    const {
        bottom: nodeBottom,
        height,
        left: nodeLeft,
        right: nodeRight,
        top: nodeTop,
        width,
    } = node.getBoundingClientRect();
    const { left: containerLeft, width: containerWidth } = container.getBoundingClientRect();

    const top = nodeTop;
    const bottom = nodeBottom;
    const left = nodeLeft - containerLeft;
    const right = nodeRight - containerLeft;
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;

    const isVisibleHorizontally =
        Math.floor(100 + (Math.min(left, 0) / width) * 100) >= VISIBILITY_PERSENTAGE &&
        Math.floor(100 - ((right - containerWidth) / width) * 100) >= VISIBILITY_PERSENTAGE;

    const isVisibleVertically =
        Math.floor(100 + (Math.min(0, top) / height) * 100) >= VISIBILITY_PERSENTAGE &&
        Math.floor(100 - ((bottom - windowHeight) / height) * 100) >= VISIBILITY_PERSENTAGE;

    return isVisibleHorizontally && isVisibleVertically;
};

const findVisibleAndSendEvent = ({
    analytics,
    cache,
    children,
    container,
}: {
    analytics: Array<TEvent | undefined>;
    cache: Record<number, number>;
    children: HTMLDivElement[];
    container: HTMLDivElement;
}) => {
    children.forEach((child, idx) => {
        if (cache[idx]) return;

        if (isElementInViewport(container, child) && analytics[idx]) {
            sendEvent(analytics[idx]);
            Object.assign(cache, { [idx]: 1 });
        }
    });
};

export function useAnalyticsView(ref: RefObject<HTMLDivElement>, children: ReactNode) {
    const viewAnalytics = useGetChildrenProp(
        children as ReactElement<{
            dataShowAnalytics?: TEvent;
        }>,
        'dataShowAnalytics'
    );

    const savedCards = useRef<HTMLDivElement[]>([]);
    const cache = useRef<Record<number, number>>({});

    useEffect(() => {
        if (viewAnalytics.length === 0) return () => {};

        const elem = ref.current;

        if (!elem) return () => {};

        savedCards.current = getCards(elem);

        findVisibleAndSendEvent({
            container: elem,
            children: savedCards.current,
            analytics: viewAnalytics,
            cache: cache.current,
        });
        const handler = debounce(300, () => {
            findVisibleAndSendEvent({
                container: elem,
                children: savedCards.current,
                analytics: viewAnalytics,
                cache: cache.current,
            });
        });

        const eventOptions: AddEventListenerOptions = { passive: true };

        elem.addEventListener('scroll', handler, eventOptions);
        window.addEventListener('scroll', handler, eventOptions);

        return () => {
            if (elem) {
                elem.removeEventListener('scroll', handler, eventOptions);
            }
            window.removeEventListener('scroll', handler, eventOptions);
        };
    }, [ref, viewAnalytics]);
}
